// CalendarSchedule.tsx
import React, { useCallback, useState } from "react";

import moment, { Moment } from "moment";
import {
  Calendar,
  Components,
  DateLocalizer,
  EventProps,
  Formats,
  HeaderProps,
  momentLocalizer,
  SlotInfo,
  View,
  ViewsProps,
} from "react-big-calendar";

import "./CalendarSchedule.scss";

import CalendarPopupEvent from "./CalendarPopupEvent";
import { ModEditScheduleProps } from "./ModEditSchedule";
import { ModScheduleAppointmentProps } from "./ModScheduleAppointment";
import { ModScheduleInfoProps } from "./ModScheduleInfo";

import {
  findMatchingProvider,
  formatHolidaysForWeekView,
  generateEventsForSelectedWeeks,
  GroupedScheduleEventType,
  HolidayType,
  ScheduleEventType,
} from "./sequence/ScheduleSetting";

moment.updateLocale("en", { week: { dow: 1 } });
const localizer = momentLocalizer(moment);

type CalendarScheduleProps = {
  divisionId: number;
  events: GroupedScheduleEventType[];
  holidays: HolidayType[];
  isMultipleWeeks: boolean;
  isProvider?: boolean;
  providerIds: number[] | null;
  rangeDate: { end: Moment; start: Moment };
  selectedEventIds: number[];
  selectedWeeks: string[];
  specialtyIds: number[] | null;
  weekStartDay?: number;
  year: number;
  onCheckbox: (ids: number[]) => void;
  onClose: () => void;
  onDelete: ModScheduleInfoProps["onDelete"];
  onEdit: ModScheduleInfoProps["onEdit"];
  onPublish: ModScheduleInfoProps["onPublish"];
  onSave: ModScheduleInfoProps["onSave"];
  onSelectChat: ModScheduleAppointmentProps["onSelectChat"];
  onSelectEvent: (event: ScheduleEventType) => void;
  onSelectProvider: ModScheduleAppointmentProps["onSelectProvider"];
  onSelectSlot: (slotInfo: SlotInfo) => void;
  onUpdateDSB: ModEditScheduleProps["onUpdateDSB"];
};

const formats: Formats = {
  dayFormat: (date: Date, culture?: string, localize?: DateLocalizer) =>
    localize?.format(date, "D", culture) || "",
  dayHeaderFormat: (date: Date, culture?: string, localize?: DateLocalizer) =>
    localize?.format(date, "ddd", culture).toUpperCase() || "",
  eventTimeRangeFormat: () => "",
  // eslint-disable-next-line no-shadow
  timeGutterFormat: (date, culture, localizer) => {
    if (localizer) {
      return localizer.format(date, "h A", culture);
    }

    return "";
  },
};

const CALENDAR_VIEWS = ["week"] as ViewsProps;

const CalendarSchedule = (props: CalendarScheduleProps) => {
  const [isResizing, setIsResizing] = useState(false); // 👈 เพิ่ม state

  const formattedHolidays = React.useMemo(
    () => formatHolidaysForWeekView(props.holidays, props.rangeDate.start),
    [props.holidays, props.rangeDate.start]
  );

  const handleSelectSlot = useCallback(
    (slotInfo: SlotInfo) => {
      const selectedDate = moment(slotInfo.start);

      const events = generateEventsForSelectedWeeks(
        {
          date: selectedDate,
          end_time: selectedDate.format("HH:mm"),
          start_time: selectedDate.format("HH:mm"),
        },
        props.selectedWeeks,
        props.weekStartDay
      );

      // เช็คว่า event ทั้งหมดเป็นวันหยุดหรือไม่
      const allEventsAreHolidays = events.every((event) => {
        const eventDate = moment(event.start);

        return formattedHolidays.some((holiday) => holiday.date.isSame(eventDate, "day"));
      });

      // เช็คว่า event ทั้งหมดเป็นวันในอดีตหรือไม่
      const allEventsArePast = events.every((event) => {
        const eventDate = moment(event.start);

        return eventDate.isBefore(moment());
      });

      const isDifferentYear = selectedDate.year() !== props.year;

      // บล็อกเฉพาะเมื่อ event ทั้งหมดเป็นวันหยุด, ทั้งหมดเป็นวันในอดีต, หรือปีไม่ตรง
      if (allEventsAreHolidays || allEventsArePast || isDifferentYear) {
        return;
      }

      props.onSelectSlot(slotInfo);
    },
    [
      formattedHolidays,
      props.isMultipleWeeks,
      props.onSelectSlot,
      props.selectedWeeks,
      props.weekStartDay,
      props.year,
    ]
  );

  const handleSelectEvent = useCallback(
    (event: GroupedScheduleEventType) => {
      const momentEvent = props.events.find((e) => e.id === event.id);

      if (momentEvent) {
        props.onSelectEvent(momentEvent);
      }
    },
    [props.events, props.onSelectEvent]
  );

  const eventStyleGetter = useCallback(
    (event: GroupedScheduleEventType) => {
      let bg = "#FFC5D9";
      let border = "var(--primary-theme-color)";

      const foundProvider = findMatchingProvider(
        event.items,
        props.providerIds,
        props.specialtyIds
      );

      if (foundProvider) {
        bg = foundProvider.dsb.profile.color.light;
        border = foundProvider.dsb.profile.color.medium;
      } else if (event.is_closed) {
        bg = "rgba(217, 217, 217, 0.78)";
        border = "rgba(217, 217, 217, 0.78)";
      } else if (event.has_provider) {
        bg = "rgba(234, 241, 251, 1)";
        border = "rgba(154, 195, 255, 1)";
      } else if (event.is_draft) {
        bg = "#ffd4df";
      }

      const style: React.CSSProperties = {
        backgroundColor: bg,
        border: `1px solid ${border}`,
        borderRadius: "2px",
        color: "#3C3C3C",
        cursor: "pointer",
        padding: 0,
      };

      return { style };
    },
    [props.providerIds]
  );

  const slotStyleGetter = useCallback(
    () => ({
      style: {
        backgroundColor: "#FFF4F7",
      },
    }),
    [formattedHolidays, props.isMultipleWeeks]
  );

  const dayStyleGetter = useCallback(
    (date: Date) => {
      const dateMoment = moment(date);
      const isHoliday = formattedHolidays.some((h) => h.date.isSame(dateMoment, "day"));
      const isDifferentYear = dateMoment.year() !== props.year;

      if (isHoliday && !props.isMultipleWeeks) {
        return {
          className: "holiday-day",
          style: {
            backgroundColor: "#D4D4D4",
          },
        };
      }

      if (isDifferentYear) {
        return {
          className: "different-year-day",
        };
      }

      return {};
    },
    [formattedHolidays, props.isMultipleWeeks, props.year]
  );

  const CustomEventWithHolidays = useCallback(
    (eventProps: EventProps<GroupedScheduleEventType>) => (
      <CalendarPopupEvent
        divisionId={props.divisionId}
        event={eventProps.event}
        isMultipleWeeks={props.isMultipleWeeks}
        isProvider={props.isProvider}
        providerIds={props.providerIds}
        selectedEventIds={props.selectedEventIds}
        specialtyIds={props.specialtyIds}
        onCheckbox={props.onCheckbox}
        onClose={props.onClose}
        onDelete={props.onDelete}
        onEdit={props.onEdit}
        onPublish={props.onPublish}
        onResizingChange={setIsResizing}
        onSave={props.onSave}
        onSelectChat={props.onSelectChat}
        onSelectProvider={props.onSelectProvider}
        onUpdateDSB={props.onUpdateDSB}
      />
    ),
    [
      props.isMultipleWeeks,
      props.onSave,
      props.providerIds,
      props.selectedEventIds,
      props.specialtyIds,
    ]
  );

  const CustomHeaderWithHolidays = useCallback(
    (headerProps: HeaderProps) => (
      <CustomHeader
        date={headerProps.date}
        holidays={formattedHolidays}
        isMultipleWeeks={props.isMultipleWeeks}
      />
    ),
    [formattedHolidays, props.isMultipleWeeks]
  );

  const components: Components<GroupedScheduleEventType> = React.useMemo(
    () => ({
      week: {
        event: CustomEventWithHolidays,
        header: CustomHeaderWithHolidays,
      },
    }),
    [CustomEventWithHolidays, CustomHeaderWithHolidays]
  );

  return (
    <div className="calendar-working-hours">
      <Calendar
        components={components}
        date={props.rangeDate.start.toDate()}
        dayPropGetter={dayStyleGetter}
        endAccessor="end"
        eventPropGetter={eventStyleGetter}
        events={props.events}
        formats={formats}
        localizer={localizer}
        longPressThreshold={0}
        max={moment().hour(23).minute(59).second(0).toDate()}
        min={moment().hour(0).minute(0).second(0).toDate()}
        selectable={!props.isProvider && !isResizing} // ปิด selectable ถ้ากำลัง resize
        slotPropGetter={slotStyleGetter}
        startAccessor="start"
        step={60}
        timeslots={1}
        toolbar={false}
        tooltipAccessor={() => ""}
        view={"week" as View}
        views={CALENDAR_VIEWS}
        onSelectEvent={handleSelectEvent}
        onSelectSlot={handleSelectSlot}
      />
    </div>
  );
};

/* ====================================================== */
/*                      CustomHeader;                     */
/* ====================================================== */
type CustomHeaderProps = {
  date: Date;
  holidays: HolidayType[];
  isMultipleWeeks: boolean;
};

const CustomHeader = (props: CustomHeaderProps) => {
  const dateMoment = moment(props.date);
  const isHoliday = props.holidays.some((h) => h.date.isSame(dateMoment, "week"));
  const dayHolidays = props.holidays.filter((h) => h.date.isSame(dateMoment, "day"));
  const isToday = dateMoment.isSame(moment(), "day");
  const dayOfWeek = dateMoment.format("ddd").toUpperCase();
  const day = dateMoment.format("D");
  const holidayClass = isHoliday ? "is-holiday" : "";
  const weekClass = props.isMultipleWeeks ? "multiple-weeks" : "";

  return (
    <div className={`custom-header ${holidayClass} ${weekClass}`}>
      <div className={`day-of-week ${isToday ? "is-today" : ""}`}>{dayOfWeek}</div>
      <div className={`day-number ${isToday ? "is-today" : ""}`}>{day}</div>
      <div style={{ flex: 1 }} />
      {dayHolidays.length > 0 && (
        <div className="holiday-label">
          {dayHolidays.slice(0, 3).map((holiday, index) => (
            <div key={index} className="holiday-name">
              {holiday.name}
            </div>
          ))}
          {dayHolidays.length > 3 && (
            <div style={{ color: "white", float: "right", margin: "-1rem 0.25rem -0.25rem" }}>
              ...
            </div>
          )}
        </div>
      )}
    </div>
  );
};

CalendarSchedule.displayName = "CalendarSchedule";

export default React.memo(CalendarSchedule);
